/**
 * Stage 73.20cz — Increment Workbench (HR Bench › Increment).
 * ----------------------------------------------------------------
 * A single page with two views:
 *   1. Upcoming — live-computed list of employees whose next
 *      increment anniversary is within N days (default 30). Each row
 *      shows last increment date, days-to-next, current CTC + status
 *      of any open proposal, and an "Open working sheet" action.
 *   2. Proposals — all proposals for the company, filterable by
 *      status. Approve / Reject / Send-back per workflow level.
 *
 * Workflow:
 *   draft → proposed → manager_approved → hod_approved → hr_finalised
 *   (with 'rejected' and 'send_back → draft' branches)
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import {
  Loader2, TrendingUp, ClipboardList, AlertTriangle, CheckCircle2, XCircle,
  RotateCcw, Save, Send, Calendar, Users as UsersIcon,
} from 'lucide-react';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_META = {
  draft:            { cls: 'bg-slate-100 text-slate-700',      label: 'Draft' },
  proposed:         { cls: 'bg-amber-100 text-amber-800',      label: 'Awaiting Manager' },
  manager_approved: { cls: 'bg-sky-100 text-sky-800',          label: 'Awaiting HOD' },
  hod_approved:     { cls: 'bg-indigo-100 text-indigo-800',    label: 'Awaiting Final HR' },
  hr_finalised:     { cls: 'bg-emerald-100 text-emerald-700',  label: 'Finalised' },
  rejected:         { cls: 'bg-red-100 text-red-700',          label: 'Rejected' },
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.draft;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase whitespace-nowrap ${m.cls}`}>{m.label}</span>;
};

const fmtRupee = (n) => n == null ? '—' : `₹ ${Number(n).toLocaleString('en-IN')}`;

const daysBadge = (d, overdue) => {
  if (d == null) return null;
  if (overdue) return <span className="inline-flex items-center gap-1 bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-semibold"><AlertTriangle className="w-3 h-3" />Overdue {Math.abs(d)}d</span>;
  if (d <= 7)  return <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-semibold">Due in {d}d</span>;
  if (d <= 15) return <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs font-semibold">In {d}d</span>;
  return <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs font-semibold">In {d}d</span>;
};

export default function IncrementWorkbench() {
  const [companyId] = useActiveCompany();
  const [tab, setTab] = useState('upcoming');
  const [days, setDays] = useState(30);
  const [statusFilter, setStatusFilter] = useState('all');

  const [upcoming, setUpcoming] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);

  // Working-sheet modal
  const [sheet, setSheet] = useState({ open: false, row: null, saving: false });
  const [form, setForm] = useState({
    proposed_ctc_annual: 0, percentage_increase: '', effective_from: '',
    reason: '', remarks: '',
  });

  // Decision modal
  const [dec, setDec] = useState({ open: false, id: '', action: '', remarks: '', saving: false });



  const loadProposals = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/hr/increment/proposals?company_id=${companyId}&status=${statusFilter}`,
        { withCredentials: true },
      );
      setProposals(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load proposals');
      setProposals([]);
    } finally { setLoading(false); }
  }, [companyId, statusFilter]);
  const loadUpcoming = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/hr/increment/upcoming?company_id=${companyId}&days=${days}`,
        { withCredentials: true },
      );
      setUpcoming(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load upcoming');
      setUpcoming([]);
    } finally { setLoading(false); }
  }, [companyId, days]);

  useEffect(() => { tab === 'upcoming' ? loadUpcoming() : loadProposals(); }, [tab, loadProposals, loadUpcoming]);

  const openSheet = (row) => {
    // Backend already surfaced current_ctc_annual + current_gross_monthly on
    // the upcoming row; no need for an extra roundtrip.
    setSheet({ open: true, row, saving: false });
    setForm({
      proposed_ctc_annual: row.current_ctc_annual || 0,
      percentage_increase: '',
      effective_from: row.next_due_date || '',
      reason: '',
      remarks: '',
    });
  };

  const saveSheet = async (submit = false) => {
    if (!form.proposed_ctc_annual || Number(form.proposed_ctc_annual) <= 0) { toast.error('Enter proposed CTC'); return; }
    if (!form.effective_from) { toast.error('Pick effective from date'); return; }
    setSheet((s) => ({ ...s, saving: true }));
    try {
      const body = {
        employee_id: sheet.row.employee_id,
        company_id: companyId,
        current_ctc_annual: Number(sheet.row.current_ctc_annual) || 0,
        proposed_ctc_annual: Number(form.proposed_ctc_annual),
        percentage_increase: form.percentage_increase === '' ? null : Number(form.percentage_increase),
        effective_from: form.effective_from,
        reason: form.reason,
        remarks: form.remarks,
      };
      const { data } = await axios.post(`${API_URL}/hr/increment/proposals`, body, { withCredentials: true });
      if (submit) {
        await axios.post(`${API_URL}/hr/increment/proposals/${data.id}/submit`, {}, { withCredentials: true });
      }
      toast.success(submit ? 'Proposal submitted for manager approval' : 'Draft saved');
      setSheet({ open: false, row: null, saving: false });
      loadUpcoming();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
      setSheet((s) => ({ ...s, saving: false }));
    }
  };

  const doAction = async () => {
    setDec((d) => ({ ...d, saving: true }));
    try {
      await axios.post(
        `${API_URL}/hr/increment/proposals/${dec.id}/action`,
        { action: dec.action, remarks: dec.remarks },
        { withCredentials: true },
      );
      toast.success(`Proposal ${dec.action}d`);
      setDec({ open: false, id: '', action: '', remarks: '', saving: false });
      loadProposals();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
      setDec((d) => ({ ...d, saving: false }));
    }
  };

  const submitDraft = async (id) => {
    try {
      await axios.post(`${API_URL}/hr/increment/proposals/${id}/submit`, {}, { withCredentials: true });
      toast.success('Submitted for manager approval');
      loadProposals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Submit failed'); }
  };

  return (
    <MainLayout>
      <div className="space-y-5" data-testid="increment-workbench-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Increment Workbench</h1>
            <p className="text-sm text-slate-600 mt-1">Track upcoming anniversary alerts, prepare working sheets, and route them through the Manager → HOD → HR approval chain.</p>
          </div>
          <div className="flex items-center gap-3">
            {tab === 'upcoming' ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-500">Alert window:</span>
                <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                  data-testid="alert-window-select"
                  className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white">
                  {[15, 30, 45, 60, 90].map((d) => <option key={d} value={d}>{`${d} days`}</option>)}
                </select>
              </div>
            ) : (
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="status-filter"
                className="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white">
                <option value="all">All statuses</option>
                {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-slate-100 rounded-lg p-1 max-w-lg">
          <button onClick={() => setTab('upcoming')} data-testid="tab-upcoming"
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium flex items-center gap-2 justify-center ${tab === 'upcoming' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <TrendingUp className="w-4 h-4" /> Upcoming
          </button>
          <button onClick={() => setTab('proposals')} data-testid="tab-proposals"
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium flex items-center gap-2 justify-center ${tab === 'proposals' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <ClipboardList className="w-4 h-4" /> Proposals
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : tab === 'upcoming' ? (
            upcoming.length === 0 ? (
              <div className="text-center text-slate-500 py-14 text-sm">No employees with an anniversary in the next {days} days.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-4 py-2.5">Employee</th>
                    <th className="text-left px-4 py-2.5">Last Increment</th>
                    <th className="text-left px-4 py-2.5">Next Due</th>
                    <th className="text-left px-4 py-2.5">Alert</th>
                    <th className="text-left px-4 py-2.5">Existing Proposal</th>
                    <th className="text-right px-4 py-2.5">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {upcoming.map((r) => (
                    <tr key={r.employee_id} className="border-b border-slate-100" data-testid={`upcoming-row-${r.employee_code}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{r.employee_name}</div>
                        <div className="text-xs text-slate-500 font-mono">{r.employee_code}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        <div>{r.last_increment_date}</div>
                        <div className="text-[10px] text-slate-400 uppercase">{r.last_increment_source === 'doj' ? 'from joining' : 'from CTC'}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{r.next_due_date}</td>
                      <td className="px-4 py-3">{daysBadge(r.days_to_next, r.overdue)}</td>
                      <td className="px-4 py-3">
                        {r.existing_proposal
                          ? <StatusPill status={r.existing_proposal.status} />
                          : <span className="text-slate-400 text-xs italic">none</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {r.existing_proposal
                          ? (
                            <button
                              onClick={() => { setTab('proposals'); setStatusFilter('all'); }}
                              className="text-[#0052FF] hover:underline text-xs">
                              View proposal
                            </button>
                          )
                          : (
                            <Button
                              onClick={() => openSheet(r)}
                              data-testid={`open-sheet-${r.employee_code}`}
                              size="sm"
                              className="bg-[#0052FF] hover:bg-[#0043D1] text-white text-xs"
                            >
                              Open working sheet
                            </Button>
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            proposals.length === 0 ? (
              <div className="text-center text-slate-500 py-14 text-sm">No proposals yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-4 py-2.5">Employee</th>
                    <th className="text-right px-4 py-2.5">Current CTC</th>
                    <th className="text-right px-4 py-2.5">Proposed CTC</th>
                    <th className="text-right px-4 py-2.5">%</th>
                    <th className="text-left px-4 py-2.5">Effective From</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                    <th className="text-right px-4 py-2.5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {proposals.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100" data-testid={`proposal-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{p.employee_name || p.employee_code}</div>
                        <div className="text-xs text-slate-500 font-mono">{p.employee_code}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700">{fmtRupee(p.current_ctc_annual)}</td>
                      <td className="px-4 py-3 text-right text-slate-900 font-medium">{fmtRupee(p.proposed_ctc_annual)}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{p.percentage_increase != null ? `${p.percentage_increase}%` : '—'}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{p.effective_from || '—'}</td>
                      <td className="px-4 py-3"><StatusPill status={p.status} /></td>
                      <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                        {p.status === 'draft' && (
                          <button data-testid={`submit-${p.id}`} onClick={() => submitDraft(p.id)}
                            className="text-[#0052FF] hover:underline text-xs font-semibold">Submit</button>
                        )}
                        {['proposed', 'manager_approved', 'hod_approved'].includes(p.status) && (
                          <>
                            <button data-testid={`approve-${p.id}`}
                              onClick={() => setDec({ open: true, id: p.id, action: 'approve', remarks: '', saving: false })}
                              className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                            </button>
                            <button data-testid={`send-back-${p.id}`}
                              onClick={() => setDec({ open: true, id: p.id, action: 'send_back', remarks: '', saving: false })}
                              className="text-amber-700 hover:text-amber-900 text-xs font-semibold inline-flex items-center gap-1">
                              <RotateCcw className="w-3.5 h-3.5" /> Send back
                            </button>
                            <button data-testid={`reject-${p.id}`}
                              onClick={() => setDec({ open: true, id: p.id, action: 'reject', remarks: '', saving: false })}
                              className="text-red-700 hover:text-red-900 text-xs font-semibold inline-flex items-center gap-1">
                              <XCircle className="w-3.5 h-3.5" /> Reject
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>

      {/* Working sheet modal */}
      {sheet.open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="working-sheet-modal">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Increment Working Sheet</h2>
                <p className="text-xs text-slate-500 mt-0.5">{sheet.row?.employee_name} · {sheet.row?.employee_code}</p>
              </div>
              <button onClick={() => setSheet({ open: false, row: null, saving: false })} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                  <p className="text-xs text-slate-500">Last Increment</p>
                  <p className="font-medium text-slate-900">{sheet.row?.last_increment_date || '—'}</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                  <p className="text-xs text-slate-500">Anniversary Due</p>
                  <p className="font-medium text-slate-900">{sheet.row?.next_due_date}</p>
                </div>
                <div className="col-span-2 bg-slate-50 border border-slate-200 rounded-md p-3">
                  <p className="text-xs text-slate-500">Current CTC (Annual)</p>
                  <p className="font-medium text-slate-900">{fmtRupee(sheet.row?.current_ctc_annual)}</p>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Proposed CTC (Annual) *</label>
                <input type="number" min="0" step="1"
                  data-testid="sheet-proposed-ctc"
                  value={form.proposed_ctc_annual} onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => {
                      const cur = Number(sheet.row?.current_ctc_annual) || 0;
                      const pct = cur > 0 && Number(v) > 0 ? (((Number(v) - cur) / cur) * 100).toFixed(2) : '';
                      return { ...f, proposed_ctc_annual: v, percentage_increase: pct };
                    });
                  }}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-700">% Increase</label>
                  <input type="number" step="0.01" value={form.percentage_increase}
                    onChange={(e) => setForm({ ...form, percentage_increase: e.target.value })}
                    data-testid="sheet-percentage"
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mt-1" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Effective From *</label>
                  <input type="date" value={form.effective_from}
                    onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                    data-testid="sheet-effective-from"
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mt-1" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Reason</label>
                <textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  data-testid="sheet-reason"
                  placeholder="e.g. annual increment, promotion, high performance"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Remarks (optional)</label>
                <textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  data-testid="sheet-remarks"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mt-1" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <Button variant="outline" onClick={() => setSheet({ open: false, row: null, saving: false })}>Cancel</Button>
              <Button onClick={() => saveSheet(false)} disabled={sheet.saving} variant="outline" data-testid="save-draft"><Save className="w-4 h-4 mr-2" /> Save Draft</Button>
              <Button onClick={() => saveSheet(true)} disabled={sheet.saving} data-testid="submit-proposal"
                className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
                {sheet.saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Submit for Approval
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Decision modal */}
      {dec.open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="decision-modal-increment">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-slate-200">
              <h3 className="text-base font-semibold text-slate-900">
                {dec.action === 'approve' ? 'Approve' : dec.action === 'reject' ? 'Reject' : 'Send Back to HR'}
              </h3>
            </div>
            <div className="p-5">
              <label className="text-xs font-medium text-slate-700">Remarks {dec.action !== 'approve' && '(required)'}</label>
              <textarea rows={4} value={dec.remarks} onChange={(e) => setDec({ ...dec, remarks: e.target.value })}
                data-testid="decision-remarks-increment"
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mt-1" />
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <Button variant="outline" onClick={() => setDec({ open: false, id: '', action: '', remarks: '', saving: false })}>Cancel</Button>
              <Button onClick={doAction} disabled={dec.saving || (dec.action !== 'approve' && !dec.remarks.trim())}
                data-testid="submit-decision-increment"
                className={
                  dec.action === 'approve'   ? 'bg-emerald-600 hover:bg-emerald-700 text-white' :
                  dec.action === 'send_back' ? 'bg-amber-600 hover:bg-amber-700 text-white' :
                                               'bg-red-600 hover:bg-red-700 text-white'
                }>
                {dec.saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
